import {Injectable} from "@angular/core";
import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";
import {DeliveryPointLocal} from "@app/core/delivery-point/domain/delivery-point.local";
import {List} from "@app/core/lists/domain/list";
import {ListGroup} from "@app/core/lists/domain/list-group";
import {ListRemote} from "@app/core/lists/domain/list.remote";
import {ListGroupMapper} from "@app/core/lists/infrastructure/mappers/list-group.mapper";
import {ListMapper} from "@app/core/lists/infrastructure/mappers/list.mapper";
import {Product} from "@app/core/product/domain/dtos/product";
import {ApiUrls} from "@app/core/shared/infrastructure/api-urls";
import {BaseHandleErrorMapper} from "@app/core/shared/infrastructure/base.handler/base-handle-error.mapper";
import {HandlerBuilder, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {EuropastryBaseHandler} from "@app/core/shared/infrastructure/base.handler/europastry-base.handler";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {catchError, filter, map, switchMap, take, tap} from "rxjs/operators";
import {ListDetail} from "../domain/list-detail";
import {ListDetailMapper} from "./mappers/list-detail.mapper";
import {UsualPurchaseMapper} from "./mappers/usual-purchase.mapper";


@Injectable({
    providedIn: 'root'
})
export class ListWebService implements ListRemote {


    // This subject is used to refresh the observable that returns the getList method.
    // This forces getList to make another request when the user adds or deletes a product from a list.
    private refresh = new ReplaySubject<void>();

    // The last ListGroup that has been loaded.
    // This allows you to delete a listGroup and update the list of listGroups without having to make the request again.
    private loadedListGroupsSubject = new BehaviorSubject<ListGroup[] | null>(null);

    // This subject is used to refresh the observable that returns the getList method.
    // This forces getList to make another request when the user adds or deletes a product from a list.
    private refreshListDetail = new ReplaySubject<void>();

    constructor(
        private baseHandler: EuropastryBaseHandler,
        private credentialsStorage: CredentialsStorage,
        private deliveryPointLocal: DeliveryPointLocal,
        private apiUrls: ApiUrls,
    ) {
        this.reloadListDetail();
    }

    /**
     * Clears the loadedListGroupsSubject and reloads the list of listGroups.
     */
    private loadListGroups() {
        this.loadedListGroupsSubject.next(null);
        this.reloadListGroups();
    }

    /**
     * Deletes the list with the given id from the loadedListGroupsSubject.
     */
    private deleteListInLoadedListGroups(listId: number): void {
        const listGroups = this.loadedListGroupsSubject.value;
        if (listGroups) {
            listGroups.forEach(listGroup => {
                listGroup.lists = listGroup.lists.filter(list => list.id !== listId);
            });
            this.loadedListGroupsSubject.next(listGroups);
        }
    }

    private addListInLoadedListGroups(list: List): void {
        const listGroups = this.loadedListGroupsSubject.value;
        if (!listGroups || listGroups.length === 0) {
            return;
        }
        const userLists = listGroups[0];
        userLists.lists.push(list);
        this.loadedListGroupsSubject.next(listGroups);
    }

    /**
     * Updates the name of the list with the given id in the loadedListGroupsSubject.
     */
    private updateLisNameInLoadedListGroups(listId: number, newName: string): void {
        const listGroups = this.loadedListGroupsSubject.value;
        if (!listGroups || listGroups.length === 0) {
            return;
        }
        const userLists = listGroups[0];
        userLists.lists.forEach(list => {
            if (list.id === listId) {
                list.name = newName;
            }
        });
        this.loadedListGroupsSubject.next(listGroups);
    }
    getListGroups(): Observable<ListGroup[]> {
        this.loadListGroups(); // Force to load the list of listGroups.
        return this.loadedListGroupsSubject.asObservable().pipe(
            filter((listGroups): listGroups is ListGroup[] => listGroups != null),
        );
    }

    /**
     * Only fetches the list of listGroups.
     */
    private fetchListGroups(): Observable<ListGroup[]> {
        const deliveryPointCode = this.deliveryPointLocal.getDeliveryPointCode();
        const parameters = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(this.apiUrls.LISTS + deliveryPointCode)
            .authorization(this.credentialsStorage.getCredentials())
            .build();

        return this.baseHandler.execute(parameters).pipe(
            map(response => response.map(ListGroupMapper.deserialize)),
            catchError(BaseHandleErrorMapper.handleError)
        );
    }

    /**
     * Fetches the list of listGroups and updates the loadedListGroupsSubject.
     */
    private reloadListGroups(): void {
        this.fetchListGroups().pipe(
            take(1)
        ).subscribe({
            next: listGroups => {
            this.loadedListGroupsSubject.next(listGroups);
            this.refresh.next();
        }});
    }

    /**
     * Send a event to refresh the response of getList method.
     */
    private reloadListDetail(): void {
        this.refreshListDetail.next();
    }

    addProductToList(listId: number, productCode: string, quantity: number): Observable<List> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.post)
                .url(this.apiUrls.LISTS + deliveryPointCode + '/' + listId)
                .authorization(this.credentialsStorage.getCredentials())
                .body({
                    code: productCode,
                    quantity: quantity
                })
                .build();

            return this.baseHandler.execute(parameters).pipe(
                map(ListMapper.deserialize),
                catchError(BaseHandleErrorMapper.handleError),
                tap(() => this.reloadListGroups()),
                tap(() => this.reloadListDetail()),
            );

        }));
    }

    deleteProductFromList(listId: number, productCode: string): Observable<List> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const parameters = new HandlerBuilder()
                .method(HttpMethod.delete)
                .url(this.apiUrls.LISTS + deliveryPointCode + '/' + listId + '/' + productCode)
                .authorization(this.credentialsStorage.getCredentials())
                .build();

            return this.baseHandler.execute(parameters).pipe(
                map(ListMapper.deserialize),
                catchError(BaseHandleErrorMapper.handleError),
                tap(() => this.reloadListGroups()),
                tap(() => this.reloadListDetail()),
            );
        }));
    }

    createList(name: string, recaptchaToken: string, productsCodes: string[]): Observable<List> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const products = productsCodes.map(code => ({
                code: code,
                quantity: 1
            }))

            const parameters = new HandlerBuilder()
                .method(HttpMethod.post)
                .url(this.apiUrls.LISTS_V2 + deliveryPointCode)
                .authorization(this.credentialsStorage.getCredentials())
                .body({
                    name: name,
                    recaptchaToken: recaptchaToken,
                    products: products,
                })
                .build();

            return this.baseHandler.execute(parameters).pipe(
                map(ListMapper.deserialize),
                catchError(BaseHandleErrorMapper.handleError),
                tap(list => this.addListInLoadedListGroups(list)),
            );

        }));
    }

    createListWithProducts(name: string, productsCodes: string[]): Observable<List> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.post)
                .url(this.apiUrls.LISTS + deliveryPointCode)
                .authorization(this.credentialsStorage.getCredentials())
                .body({
                    name: name,
                    products: productsCodes.map(productCode => { return { code: productCode, quantity: 1 } })
                })
                .build();

            return this.baseHandler.execute(parameters).pipe(
                map(ListMapper.deserialize),
                catchError(BaseHandleErrorMapper.handleError),
                tap(list => this.addListInLoadedListGroups(list))
            );

        }));
    }

    renameList(listId: number, newName: string): Observable<List> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {

            const parameters = new HandlerBuilder()
                .method(HttpMethod.post)
                .url(this.apiUrls.LISTS + deliveryPointCode)
                .authorization(this.credentialsStorage.getCredentials())
                .body({
                    id: listId,
                    name: newName
                })
                .build();

            return this.baseHandler.execute(parameters).pipe(
                map(ListMapper.deserialize),
                catchError(BaseHandleErrorMapper.handleError),
                tap(() => this.updateLisNameInLoadedListGroups(listId, newName)),
                tap(() => this.reloadListDetail()),
            );

        }));
    }

    deleteList(listId: number): Observable<void> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const parameters = new HandlerBuilder()
                .method(HttpMethod.delete)
                .url(this.apiUrls.LISTS + deliveryPointCode + '/' + listId)
                .authorization(this.credentialsStorage.getCredentials())
                .build();

            return this.baseHandler.execute(parameters).pipe(
                catchError(BaseHandleErrorMapper.handleError),
                tap(() => this.deleteListInLoadedListGroups(listId)),
            );
        }));
    }

    getList(listCode: number): Observable<ListDetail> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .url(this.apiUrls.LIST_DETAIL + deliveryPointCode + '/' + listCode + '/' + 0 + '/' + 0)
                .authorization(this.credentialsStorage.getCredentials())
                .build();

            const response$ = this.baseHandler.execute(parameters).pipe(
                map((response) => ListDetailMapper.deserialize(response.results)),
                catchError(BaseHandleErrorMapper.handleError),
            );

            return this.refreshListDetail.pipe(switchMap(() => response$));

        }));
    }

    getUsualPurchase(withStockAvailability: boolean): Observable<ListDetail> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(
            filter(() =>  this.deliveryPointLocal.getSavedDeliveryPoint() != null),
            switchMap(deliveryPointCode => {

                const parameters = new HandlerBuilder()
                    .method(HttpMethod.get)
                    .url(this.apiUrls.USUAL_PURCHASE + deliveryPointCode + '/' + withStockAvailability)
                    .authorization(this.credentialsStorage.getCredentials())
                    .build();

                return this.baseHandler.execute(parameters).pipe(
                    map((response) => UsualPurchaseMapper.deserialize(response.results)),
                    catchError(BaseHandleErrorMapper.handleError)
                );
        }));
    }

    getFavoriteProducts(itemsPerPage: number, page: number): Observable<Product[]> {

        //TODO: replace FAV_LIST_KEY
        const FAV_LIST_KEY = '3921';

        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(switchMap(deliveryPointCode => {
            const parameters = new HandlerBuilder()
                .method(HttpMethod.get)
                .url(this.apiUrls.LIST_DETAIL + deliveryPointCode + '/' + FAV_LIST_KEY + '/' + itemsPerPage + '/' + page)
                .authorization(this.credentialsStorage.getCredentials())
                .build();

            return this.baseHandler.execute(parameters).pipe(
                map(response => ListDetailMapper.deserialize(response.results)),
                map(list => list.products),
                catchError(BaseHandleErrorMapper.handleError)
            );
        }));
    }
}
